import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, Button, Paper, Alert, CircularProgress } from "@mui/material";
import { useApiWithHandling } from "../../hooks/useApiWithHandling";
import { isArchiveDocument } from "../../utils/archiveDocumentUtils";
import { isHtmlDocument } from "../../utils/htmlDocumentUtils";

export interface DocumentFile {
  id: string;
  name: string;
  type: string;
  size: string;
}

interface DocumentViewerProps {
  selectedDoc?: DocumentFile | null;
  onClose?: () => void;
  downloadEndpointBuilder?: (id: string) => string;
  backPath?: string;
  title?: string;
}

const isPlayableVideoMimeType = (mimeType: string | null): boolean => {
  if (!mimeType) {
    return false;
  }

  const normalized = mimeType.toLowerCase();
  return (
    normalized.includes("video/mp4") ||
    normalized.includes("video/webm") ||
    normalized.includes("video/ogg")
  );
};

const isAviFile = (fileName?: string | null, mimeType?: string | null): boolean => {
  const name = (fileName ?? "").toLowerCase();
  const mime = (mimeType ?? "").toLowerCase();

  return (
    name.endsWith(".avi") ||
    mime.includes("video/x-msvideo") ||
    mime.includes("video/avi") ||
    mime.includes("video/msvideo")
  );
};

const isDownloadOnlyFile = (
  fileName?: string | null,
  mimeType?: string | null
): boolean =>
  isAviFile(fileName, mimeType) ||
  isArchiveDocument(fileName, mimeType) ||
  isHtmlDocument(fileName, mimeType);

const getDownloadOnlyMessage = (
  fileName?: string | null,
  mimeType?: string | null
): string => {
  if (isHtmlDocument(fileName, mimeType)) {
    return "Preview is not available for HTML files. Please download the file to view.";
  }

  if (isArchiveDocument(fileName, mimeType)) {
    return "Preview is not available for archive files. Please download the file to view.";
  }

  return "Please download the AVI file to view.";
};

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  selectedDoc,
  onClose,
  downloadEndpointBuilder,
  backPath,
  title = "Document Viewer",
}) => {
  const routeParams = useParams<{ id: string }>();
  const documentId = selectedDoc?.id ?? routeParams.id ?? null;
  const navigate = useNavigate();
  const { getBlob } = useApiWithHandling();

  const [error, setError] = useState<string | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [contentType, setContentType] = useState<string | null>(null);
  const [downloadFileName, setDownloadFileName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [requiresDownloadOnly, setRequiresDownloadOnly] = useState(false);

  const selectedDocIsDownloadOnly = useMemo(
    () => isDownloadOnlyFile(selectedDoc?.name, selectedDoc?.type),
    [selectedDoc?.name, selectedDoc?.type]
  );

  const fetchDocumentDetails = useCallback(
    async (id: string) => {
      setError(null);
      setIsLoading(true);
      setRequiresDownloadOnly(false);

      try {
        setFileUrl((previousUrl) => {
          if (previousUrl) {
            URL.revokeObjectURL(previousUrl);
          }
          return null;
        });
        setContentType(null);
        setDownloadFileName(null);

        // AVI, archives, and HTML are not previewable in-browser — show download prompt.
        if (selectedDocIsDownloadOnly) {
          setDownloadFileName(selectedDoc?.name ?? null);
          setRequiresDownloadOnly(true);
          setIsLoading(false);
          return;
        }

        // Preview converts HEIC for the browser; MP4 and other types stream as stored.
        const endpoint = downloadEndpointBuilder
          ? downloadEndpointBuilder(id)
          : `api/documents/${id}/download?preview=true`;

        await getBlob(endpoint, {
          showLoader: false,
          showErrorSnackbar: false,
          showSuccessSnackbar: false,
          onSuccess: (blob, responseContentType, fileName) => {
            const resolvedContentType =
              responseContentType || blob.type || "application/octet-stream";
            const resolvedFileName = fileName || selectedDoc?.name || null;

            if (isDownloadOnlyFile(resolvedFileName, resolvedContentType)) {
              setDownloadFileName(resolvedFileName);
              setRequiresDownloadOnly(true);
              setIsLoading(false);
              return;
            }

            const typedBlob =
              blob.type === resolvedContentType
                ? blob
                : new Blob([blob], { type: resolvedContentType });
            const url = URL.createObjectURL(typedBlob);
            setFileUrl(url);
            setContentType(resolvedContentType);
            setDownloadFileName(resolvedFileName);
            setIsLoading(false);
          },
          onError: (err) => {
            setError(
              `Failed to load document file: ${err.message || "Unknown error"}`
            );
            setIsLoading(false);
          },
        });
      } catch (err) {
        setError(`Unexpected error: ${(err as Error).message}`);
        setIsLoading(false);
      }
    },
    [downloadEndpointBuilder, getBlob, selectedDoc?.name, selectedDocIsDownloadOnly]
  );

  useEffect(() => {
    if (documentId) {
      fetchDocumentDetails(documentId);
    }
  }, [documentId, fetchDocumentDetails]);

  useEffect(() => {
    return () => {
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl);
      }
    };
  }, [fileUrl]);

  const handleClose = () => {
    if (fileUrl) {
      URL.revokeObjectURL(fileUrl);
    }

    if (onClose) {
      onClose();
    } else {
      navigate(backPath ?? "/document/search");
    }
  };

  const handleFileViewerError = () => {
    setError(
      "Unable to display this file type. The file may be corrupted or in an unsupported format."
    );
  };

  const handleDownload = async () => {
    if (!documentId) {
      return;
    }

    // Always download the original stored file (keeps original extension).
    const endpoint = downloadEndpointBuilder
      ? downloadEndpointBuilder(documentId)
      : `api/documents/${documentId}/download`;

    await getBlob(endpoint, {
      showLoader: true,
      showErrorSnackbar: true,
      showSuccessSnackbar: false,
      onSuccess: (blob, _contentType, fileName) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download =
          fileName ||
          downloadFileName ||
          selectedDoc?.name ||
          `document_${documentId}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      },
    });
  };

  const canPreview = Boolean(fileUrl);
  const showVideoPlayer = isPlayableVideoMimeType(contentType);
  const showDownloadAction = canPreview || requiresDownloadOnly;
  const downloadOnlyMessage = getDownloadOnlyMessage(
    downloadFileName || selectedDoc?.name,
    contentType || selectedDoc?.type
  );

  if (error) {
    return (
      <Paper
        sx={{
          height: "100%",
          p: 2,
          border: "2px solid white",
          background: "none",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: "flex", gap: 2, pt: 2, borderTop: "1px solid rgba(255,255,255,0.2)" }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{ color: "white", borderColor: "white" }}
          >
            Back
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        height: "100%",
        minHeight: 0,
        p: 2,
        border: "2px solid white",
        background: "none",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {requiresDownloadOnly ? (
        <Box
          sx={{
            color: "#aaa",
            textAlign: "center",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            px: 2,
          }}
        >
          <Alert severity="info" sx={{ maxWidth: 480, textAlign: "left" }}>
            {downloadOnlyMessage}
          </Alert>
          <Typography variant="body2">
            {downloadFileName || selectedDoc?.name || "File"}
          </Typography>
        </Box>
      ) : canPreview ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              position: "relative",
              border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: 1,
              overflow: "hidden",
              backgroundColor: showVideoPlayer ? "#000" : "#fff",
            }}
          >
            {showVideoPlayer ? (
              <video
                src={fileUrl!}
                controls
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  backgroundColor: "#000",
                }}
                title={title}
                onError={handleFileViewerError}
              />
            ) : (
              <iframe
                src={fileUrl!}
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  border: "none",
                  display: "block",
                  backgroundColor: "#fff",
                }}
                title={title}
                onError={handleFileViewerError}
              />
            )}
          </Box>
        </Box>
      ) : isLoading ? (
        <Box
          sx={{
            color: "#aaa",
            textAlign: "center",
            mt: 8,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <CircularProgress size={30} sx={{ color: "hsl(19 101% 51% / 1)" }} />
          <Typography variant="body2">Loading document...</Typography>
        </Box>
      ) : (
        <Box
          sx={{
            color: "#aaa",
            textAlign: "center",
            verticalAlign: "middle",
            mt: 8,
            flex: 1,
          }}
        >
          <Typography variant="body2">No document selected</Typography>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          gap: 2,
          mt: 2,
          pt: 2,
          flexShrink: 0,
          borderTop: "1px solid rgba(255,255,255,0.2)",
        }}
      >
        <Button
          variant="outlined"
          onClick={handleClose}
          sx={{ color: "white", borderColor: "white" }}
        >
          {onClose ? "Close" : "Back"}
        </Button>

        {showDownloadAction && (
          <Button
            variant="contained"
            onClick={handleDownload}
            sx={{
              backgroundColor: "hsl(19 101% 51% / 1)",
              "&:hover": { backgroundColor: "hsl(19 101% 41% / 1)" },
            }}
          >
            Download
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default DocumentViewer;
